import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { Dietitian } from '../../../../data/dietitian/domain/dietitian';
import { PatientGroup } from '../../../../data/dietitian/domain/patient_group/patient_group';
import { Patient } from '../../../../data/patient/domain/patient';
import { DiaryType } from '../../../../data/tracker/domain/diary_type/diary_type';
import { DiaryTypeCommands } from '../../../../data/tracker/domain/diary_type/diary_type_commands';
import {
  Tracker,
  TrackerProps,
} from '../../../../data/tracker/domain/tracker/tracker';
import { TrackerCommands } from '../../../../data/tracker/domain/tracker/tracker_commands';
import { TrackerType } from '../../../../data/tracker/domain/tracker/tracker_type';

export interface TrackerCellModelsWrapper {
  origin: 'PATIENT' | 'PATIENT_GROUP' | 'DIETITIAN' | 'UNKNOWN';
  trackerCellModels: TrackerCellModel[];
}

export class TrackerCellModel {
  entity: Tracker;
  checkboxChanged: Subject<void>;
  checked: boolean;

  constructor(tracker: Tracker) {
    this.entity = tracker;
    this.checked = !!this.entity.activatedAt;
  }

  get protected() {
    return this.entity.protected;
  }

  get name() {
    return this.entity.name;
  }

  get description(): string {
    switch (this.entity.type) {
      case TrackerType.Note:
        return `note de ${this.entity.min} à ${this.entity.max}`;
      case TrackerType.Number:
        return `nombre de ${this.entity.min} à ${this.entity.max}`;
      case TrackerType.Percent:
        return `pourcentage de ${this.entity.min} à ${this.entity.max}`;
      default:
        return '';
    }
  }

  static fromTracker(tracker: Tracker): TrackerCellModel {
    return new TrackerCellModel(tracker);
  }
}

@Injectable()
export class TrackerManagerService {
  constructor(
    private trackerCommands: TrackerCommands,
    private diaryTypeCommands: DiaryTypeCommands,
  ) {
    // Nada
  }

  get trackerEvents() {
    return this.trackerCommands.getTrackerEvent();
  }

  async constructTrackers(
    dietitian: Dietitian | undefined,
    patient: Patient | undefined,
  ) {
    if (dietitian) {
      const result: TrackerCellModelsWrapper = {
        origin: patient ? 'PATIENT' : 'DIETITIAN',
        trackerCellModels: [],
      };
      const defaultTrackers = await this.trackerCommands.createDefaultTrackers(
        dietitian.dietitianId.id.toString(),
        patient?.id.toString(),
        patient?.userId?.id.toString(),
      );
      defaultTrackers.forEach((item) =>
        result.trackerCellModels.push(TrackerCellModel.fromTracker(item)),
      );
      const customTrackers = await this.trackerCommands.createCustomTrackers(
        dietitian.dietitianId.id.toString(),
        patient?.id.toString(),
        patient?.userId?.id.toString(),
      );
      let index = 100;
      customTrackers.forEach((item) => {
        item.sort = index++;
        result.trackerCellModels.push(TrackerCellModel.fromTracker(item));
      });
      return result;
    }
    throw new Error('Diététicien.ne non identifié.e !');
  }

  async getEvents(
    dietitianId: string | undefined,
    groupId: string | undefined,
    patientId: string | undefined,
  ): Promise<DiaryType[]> {
    return this.diaryTypeCommands.findAllDiaryTypes(
      dietitianId,
      groupId,
      patientId,
    );
  }

  async getTrackers(
    dietitianId: string | undefined,
    patientGroupId: string | undefined,
    patientId: string | undefined,
  ): Promise<TrackerCellModelsWrapper> {
    const result = await this.trackerCommands.getTrackers(
      dietitianId,
      patientGroupId,
      patientId,
    );
    return {
      origin: result.origin,
      trackerCellModels: result?.trackers.map((tracker) =>
        TrackerCellModel.fromTracker(tracker),
      ),
    };
  }

  async countActiveTrackers(
    currentTrackers: TrackerCellModelsWrapper,
  ): Promise<{ [key: string]: number } | undefined> {
    if (currentTrackers) {
      return {
        collation: this.getActiveTrackersCount(
          currentTrackers.trackerCellModels
            .map((tcm) => tcm.entity)
            .filter((t) => t.diaryType === 'collation'),
        ),
        feeling: this.getActiveTrackersCount(
          currentTrackers.trackerCellModels
            .map((tcm) => tcm.entity)
            .filter((t) => t.diaryType === 'feeling'),
        ),
        hydratation: this.getActiveTrackersCount(
          currentTrackers.trackerCellModels
            .map((tcm) => tcm.entity)
            .filter((t) => t.diaryType === 'hydratation'),
        ),
        meal: this.getActiveTrackersCount(
          currentTrackers.trackerCellModels
            .map((tcm) => tcm.entity)
            .filter((t) => t.diaryType === 'meal'),
        ),
        physical_activity: this.getActiveTrackersCount(
          currentTrackers.trackerCellModels
            .map((tcm) => tcm.entity)
            .filter((t) => t.diaryType === 'physical_activity'),
        ),
        challenge_of_the_day: this.getActiveTrackersCount(
          currentTrackers.trackerCellModels
            .map((tcm) => tcm.entity)
            .filter((t) => t.diaryType === 'challenge_of_the_day'),
        ),
        sleep: this.getActiveTrackersCount(
          currentTrackers.trackerCellModels
            .map((tcm) => tcm.entity)
            .filter((t) => t.diaryType === 'sleep'),
        ),
        excluding_meals: this.getActiveTrackersCount(
          currentTrackers.trackerCellModels
            .map((tcm) => tcm.entity)
            .filter((t) => t.diaryType === 'excluding_meals'),
        ),
        symptoms: this.getActiveTrackersCount(
          currentTrackers.trackerCellModels
            .map((tcm) => tcm.entity)
            .filter((t) => t.diaryType === 'symptoms'),
        ),
      };
    }
    return undefined;
  }

  toggleTracker(trackerProps: TrackerProps, trackerId: string) {
    return this.trackerCommands.saveTracker(trackerProps, trackerId, false);
  }

  removeTracker(
    dietitianId: string | undefined,
    patientGroupId: string | undefined,
    patientId: string | undefined,
    trackerId: string,
  ) {
    return this.trackerCommands.deleteTracker(
      dietitianId,
      patientGroupId,
      patientId,
      trackerId,
    );
  }

  saveTracker(
    trackerProps: TrackerProps,
    trackerId?: string,
  ): Promise<Tracker> {
    return this.trackerCommands.saveTracker(trackerProps, trackerId);
  }

  async saveTrackers(trackers: Tracker[]): Promise<void> {
    return this.trackerCommands.saveTrackers(trackers);
  }

  private getActiveTrackersCount(trackers: Tracker[]) {
    return trackers.filter((tracker) => tracker.active).length;
  }

  async saveTrackersGroup(
    events: DiaryType[],
    trackers: TrackerCellModelsWrapper,
    group: PatientGroup,
  ): Promise<TrackerCellModelsWrapper> {
    await this.trackerCommands.saveGroupBatch(trackers, group);
    await this.diaryTypeCommands.saveAllDiaryTypeToGroup(
      events,
      group.id.toString(),
    );
    return trackers;
  }

  async saveTrackersPatient(
    trackers: TrackerCellModelsWrapper,
    patient: Patient,
  ) {
    await this.trackerCommands.savePatientBatch(trackers, patient);
    return trackers;
  }

  async setEvent(
    diaryType: DiaryType,
    dietitianId: string | undefined,
    patientGroupId: string | undefined,
    patientId: string | undefined,
  ) {
    return this.diaryTypeCommands.saveDiaryType(
      diaryType,
      dietitianId,
      patientGroupId,
      patientId,
    );
  }
}
